import {
  CloudOff,
  MobileScreenShare,
  MoreVert,
  RemoveRedEye,
} from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  NotificationTypeEnum,
  useDialog,
  useMenu,
  useNotification,
} from '@prismamedia/one-components';
import { FC, MouseEvent, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useArticleUpdater } from '../../apollo/mutations/article.web.graphql';
import { ArticleFormat, ArticleStatus } from '../../__generated__/queries-web';
import {
  ERROR_MODIFY_STATUS,
  SEE_PUBLIC_SITE,
  SEND_PUSH,
  STATUS_TO_DRAFT,
  UNPUBLISH,
  UNPUBLISH_VIDEO,
} from './constants';
import { useStyles } from './styles';
import { useLazyQuery, ApolloError } from '@apollo/client';
import { PREVIEW_URL_STATUS } from '../../apollo/queries/previewUrlStatus.web.graphql';
import { PublicationStatusDialog } from '../PublicationStatusDialog';

interface ArticleMenuProps {
  articleId: string;
  articleFormat: ArticleFormat;
  publicUrl: string;
  brandKey: string;
  displayUnPublish: boolean;
  displaySeeArticle: boolean;
  displayPushNotification: boolean;
}

export const ArticleMenu: FC<ArticleMenuProps> = ({
  articleId,
  articleFormat,
  brandKey,
  displayUnPublish,
  displaySeeArticle,
  displayPushNotification,
  publicUrl,
}) => {
  const { openMenu, closeMenu } = useMenu();
  const { openDialog, closeDialog } = useDialog();
  const [upsert] = useArticleUpdater();
  const { pushNotification } = useNotification();
  const history = useHistory();
  const [urlToOpen, setUrlToOpen] = useState('');

  const classes = useStyles();

  const [checkUrlStatus] = useLazyQuery(PREVIEW_URL_STATUS, {
    onCompleted: (responseData) => {
      if (responseData?.previewUrlStatus?.status >= 400) {
        openDialog(
          <PublicationStatusDialog url={urlToOpen} onClose={closeDialog} />,
          {
            maxWidth: 'sm',
          },
        );
      } else {
        // Status is OK, open the URL
        window.open(urlToOpen);
      }
    },
    onError: (queryError: ApolloError) => {
      console.error('Error checking URL status:', queryError);
      // If query fails, just open the URL
      window.open(urlToOpen);
    },
  });

  const executeUnpublish = useCallback(async () => {
    try {
      const props = {
        variables: {
          where: { id: articleId },
          data: {
            status:
              articleFormat === ArticleFormat.Video
                ? ArticleStatus.Deleted
                : ArticleStatus.Draft,
          },
        },
      };
      const result = await upsert(props);
      if (result?.data?.updateArticle) {
        pushNotification({
          message: STATUS_TO_DRAFT,
          type: NotificationTypeEnum.success,
        });
      }
    } catch {
      pushNotification({
        message: ERROR_MODIFY_STATUS,
        type: NotificationTypeEnum.error,
      });
    }
  }, [articleId, pushNotification, upsert, articleFormat]);

  const handleMutation = useCallback(
    async (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      closeMenu();

      openDialog(
        <>
          <DialogTitle>Dépublier un article</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ⚠️ Attention : Êtes-vous sûr(e) de vouloir dépublier cet article ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} color="primary" variant="outlined">
              Annuler
            </Button>
            <Button
              onClick={() => {
                executeUnpublish();
                closeDialog();
              }}
              color="primary"
              variant="contained"
              autoFocus
            >
              Oui
            </Button>
          </DialogActions>
        </>,
      );
    },
    [closeMenu, openDialog, closeDialog, executeUnpublish],
  );

  const handleMobilePush = () => {
    closeMenu();
    history.push(`/${brandKey}/mobilepush/add?id=${articleId}`);
  };

  const UnPublishItem = () => {
    return (
      <MenuItem
        classes={{ root: classes.actionsMenu }}
        data-testid="unPublish"
        onClick={handleMutation}
      >
        <ListItemIcon>
          <CloudOff fontSize="small" />
        </ListItemIcon>
        <Typography variant="inherit">
          {articleFormat === ArticleFormat.Video ? UNPUBLISH_VIDEO : UNPUBLISH}
        </Typography>
      </MenuItem>
    );
  };

  const DisplayItem = () => {
    return (
      <MenuItem
        classes={{ root: classes.actionsMenu }}
        data-testid="seeArticle"
        onClick={() => {
          closeMenu();
          if (config.ENV === 'REC2') {
            window.open(publicUrl);
            return;
          }
          setUrlToOpen(publicUrl);
          checkUrlStatus({
            variables: { url: publicUrl },
          });
        }}
      >
        <ListItemIcon>
          <RemoveRedEye fontSize="small" />
        </ListItemIcon>
        <Typography variant="inherit">{SEE_PUBLIC_SITE}</Typography>
      </MenuItem>
    );
  };

  const SendPushItem = () => {
    return (
      <MenuItem
        classes={{ root: classes.actionsMenu }}
        data-testid="pushButton"
        onClick={handleMobilePush}
      >
        <ListItemIcon>
          <MobileScreenShare fontSize="small" />
        </ListItemIcon>
        <Typography variant="inherit">{SEND_PUSH}</Typography>
      </MenuItem>
    );
  };

  return (
    <IconButton
      arial-label="more"
      data-testid="menuBtn"
      onClick={(event) => {
        event.preventDefault();
        openMenu(
          event.currentTarget,
          <div>
            {displayUnPublish && <UnPublishItem />}
            {displaySeeArticle && <DisplayItem />}
            {displayPushNotification && <SendPushItem />}
          </div>,
        );
      }}
    >
      <MoreVert />
    </IconButton>
  );
};
